import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type GiftCardType = "fixedValue" | "customValue";

type SelectionGiftCard = {
  type: GiftCardType;
  amount: number;
  giftCard: string;
};

type SetGiftCardProperties = {
  giftCard: string;
  to: string;
  from: string;
  message?: string;
} & ({ type: "fixedValue" } | { type: "customValue"; amount: number });

type GiftCardStoreProperties = Partial<{
  type: GiftCardType;
  amount: number;
  giftCard: string;
  to: string;
  from: string;
  message: string;
}>;

type GiftCardStoreFunctions = {
  clear: () => void;
  setDetails: (giftCard: SetGiftCardProperties) => void;
  verify: (selectionGiftCard: SelectionGiftCard) => boolean;
};

type GiftCardStore = GiftCardStoreProperties & GiftCardStoreFunctions;

const fieldIsValid = <T>(selectValue: T, storedValue: T): Boolean => {
  if (!selectValue && !storedValue) return true;
  if (!selectValue || !storedValue) return false;
  return selectValue === storedValue;
};

export const useGiftCardStore = create<GiftCardStore>()(
  persist(
    (set, get) => ({
      type: undefined,
      amount: undefined,
      giftCard: undefined,
      to: undefined,
      from: undefined,
      message: undefined,
      clear: () =>
        set({
          type: undefined,
          amount: undefined,
          giftCard: undefined,
          to: undefined,
          from: undefined,
          message: undefined,
        }),
      setDetails: (giftCard: SetGiftCardProperties) => {
        const updatedValues: GiftCardStoreProperties = {
          giftCard: giftCard.giftCard,
          type: giftCard.type,
          to: giftCard.to,
          from: giftCard.from,
          message: giftCard.message,
        };

        if (giftCard.type === "customValue") {
          updatedValues.amount = giftCard.amount;
        }

        set(updatedValues);
      },
      verify: (selectionGC: SelectionGiftCard) => {
        if (
          !fieldIsValid(selectionGC.giftCard, get().giftCard) ||
          !fieldIsValid(selectionGC.type, get().type) ||
          !fieldIsValid(selectionGC.amount, get().amount)
        ) {
          get().clear();
          return false;
        }
        return true;
      },
    }),
    {
      name: "gift-card-storage",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

export const useGiftCardDetails = () => {
  const storedGiftCardDetails = useGiftCardStore((state) => ({
    "gift-card": state.giftCard,
    type: state.type,
    amount: state.amount,
    to: state.to,
    from: state.from,
    message: state.message,
  }));

  return storedGiftCardDetails;
};
